import { Component, DoCheck, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { SemControlComponent } from '../sem-control/sem-control.component';
import { RequestModel } from 'src/app/models/request/request-model';

/**
 * What the form definition carries for one relationship. Written by the form builder, read here.
 */
interface RelatedEntitiesSettings {
  Title: string;
  LinkClass: string;
  ParentProperty: string;
  TargetProperty: string;
  RenderMode: string;
  TitleStyle: string;
  AllowAdd: boolean;
  AllowRemove: boolean;
}

interface LinkValue {
  Column: string;
  Label: string;
  Value: string;
}

interface LinkRow {
  Id: number;
  TargetId: number;
  TargetTitle: string;
  Values: LinkValue[];
}

interface TargetOption {
  Id: number;
  Title: string;
}

interface RelatedEntitiesLabels {
  Link: string;
  Add: string;
  Remove: string;
  Pick: string;
  Empty: string;
  SaveFirst: string;
}

/** Used only when the server sent no labels (an older server); the server's values win. */
const DEFAULT_LABELS: RelatedEntitiesLabels = {
  Link: 'Povezava',
  Add: 'Dodaj',
  Remove: 'Odstrani',
  Pick: 'Izberi …',
  Empty: 'Ni povezav.',
  SaveFirst: 'Zapis mora biti shranjen, preden mu lahko dodate povezave.',
};

/**
 * Shows and edits the rows of a link class that belong to the record on screen.
 *
 * The control binds no model property. It is dropped on a form, told which link class to read and
 * which two of its properties are the ends of the relationship, and it works out everything else
 * from the server. Nothing is added to the record's own class, which is why a relationship can be
 * put on a compiled form - Item's - without Item being recompiled.
 *
 * It also owns its own persistence, like the files panel does and unlike a bound field: a pick is
 * written when it is made rather than when the form is saved. A control that binds no property has
 * nothing for the form's save to carry, so waiting for SHRANI would mean the pick was never stored.
 */
@Component({
  selector: 'app-sem-related-entities',
  templateUrl: './sem-related-entities.component.html',
  styleUrls: ['./sem-related-entities.component.css'],
  standalone: false
})
export class SemRelatedEntitiesComponent extends SemControlComponent
  implements OnInit, DoCheck, OnDestroy {

  @Input() settingsJson: string;
  /** Captions resolved server-side in the current language (SemRelatedEntitiesControl.GetStartTag). */
  @Input() labelsJson: string;

  settings: RelatedEntitiesSettings;
  labels: RelatedEntitiesLabels = { ...DEFAULT_LABELS };
  configurationError: string = null;
  serverError: string = null;

  rows: LinkRow[] = [];
  columns: { Column: string; Label: string }[] = [];
  options: TargetOption[] = [];

  /** Combobox mode holds one value. Null is a legitimate value and means the link is cleared. */
  selectedTargetId: number = null;

  /** List mode's picker, kept apart from the combobox value so the two modes never share state. */
  pendingTargetId: number = null;

  busy = false;

  private currentParentId: number = null;
  private lastModel: any = null;
  private subscriptions: Subscription[] = [];

  ngOnInit() {
    super.ngOnInit();

    this.labels = this.parseLabels();
    this.settings = this.parseSettings();
    if (this.configurationError) {
      return;
    }

    this.subscriptions.push(
      this.restProviderService.modelValueChanged.subscribe(() => this.syncToModel())
    );
  }

  /**
   * The model input is assigned after the form has rendered, and edit mode is a flag with no event
   * to subscribe to. Both are picked up here, the way the other content controls do it.
   */
  ngDoCheck() {
    if (this.model !== this.lastModel) {
      this.lastModel = this.model;
      this.syncToModel();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  private parseLabels(): RelatedEntitiesLabels {
    if (!this.labelsJson) {
      return { ...DEFAULT_LABELS };
    }
    try {
      const parsed = JSON.parse(this.labelsJson) || {};
      return { ...DEFAULT_LABELS, ...parsed };
    } catch {
      return { ...DEFAULT_LABELS };
    }
  }

  /** Combobox clear (the standard dropdown's x): same write as choosing nothing. */
  clearCombobox(event: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.canEdit || !this.hasParent || this.busy) {
      return;
    }
    this.selectedTargetId = null;
    this.onComboboxChange(null);
  }

  get isList(): boolean {
    return this.settings && (this.settings.RenderMode || '').toLowerCase() === 'list';
  }

  /** Title drawn as a section heading (text + line) instead of a field caption. */
  get titleAsSection(): boolean {
    return this.settings && (this.settings.TitleStyle || '').toLowerCase() === 'section';
  }

  get canEdit(): boolean {
    if (this.dfms && this.dfms.isFormReadOnly === true) {
      return false;
    }
    return !(this.isReadOnly === true || this.isReadOnly === 'true');
  }

  /**
   * A record that has not been saved yet has no id, and a link row has nothing to point at. Saying
   * so is better than an empty list, which reads as "this record has no producers".
   */
  get hasParent(): boolean {
    return this.currentParentId != null && this.currentParentId > 0;
  }

  private parseSettings(): RelatedEntitiesSettings {
    const defaults: RelatedEntitiesSettings = {
      Title: '',
      LinkClass: '',
      ParentProperty: '',
      TargetProperty: '',
      RenderMode: 'Combobox',
      TitleStyle: 'Caption',
      AllowAdd: true,
      AllowRemove: true
    };

    if (!this.settingsJson) {
      this.configurationError = 'Kontrola povezav nima nastavitev.';
      return defaults;
    }

    let parsed: RelatedEntitiesSettings;
    try {
      parsed = Object.assign(defaults, JSON.parse(this.settingsJson)) as RelatedEntitiesSettings;
    } catch (e) {
      console.error('sem-related-entities: cannot parse settings json', e);
      this.configurationError = 'Nastavitve kontrole povezav niso berljive.';
      return defaults;
    }

    // A control missing either end cannot read anything, and an empty list would look like a
    // record with no links rather than like a form that was never finished being configured.
    const missing: string[] = [];
    if (!parsed.LinkClass) { missing.push('LinkClass'); }
    if (!parsed.ParentProperty) { missing.push('ParentProperty'); }
    if (!parsed.TargetProperty) { missing.push('TargetProperty'); }
    if (missing.length > 0) {
      this.configurationError = 'Kontrola povezav ni nastavljena: manjka ' + missing.join(', ') + '.';
    }

    return parsed;
  }

  private syncToModel() {
    if (this.configurationError) {
      return;
    }

    const id = this.model && this.model.Id ? Number(this.model.Id) : null;
    if (id === this.currentParentId) {
      return;
    }

    this.currentParentId = id;
    this.rows = [];
    this.selectedTargetId = null;
    this.pendingTargetId = null;

    if (!this.hasParent) {
      return;
    }

    this.loadRows();
    this.loadOptions();
  }

  private body(extra: any = {}): any {
    return Object.assign({
      LinkClass: this.settings.LinkClass,
      ParentProperty: this.settings.ParentProperty,
      TargetProperty: this.settings.TargetProperty,
      ParentId: this.currentParentId
    }, extra);
  }

  private post(url: string, data: any, onOk: (r: any) => void) {
    const request = new RequestModel();
    request.url = url;
    request.contentType = 'application/json';
    request.data = data;

    this.busy = true;
    this.restProviderService.getDataPOST(request).subscribe({
      next: (response: any) => {
        this.busy = false;
        this.serverError = null;
        onOk(response);
      },
      error: (err: any) => {
        this.busy = false;
        this.serverError = (err && err.error && err.error.Error)
          ? err.error.Error
          : 'Povezav ni bilo mogoce shraniti.';
        console.error('sem-related-entities: ' + url + ' failed', err);
      }
    });
  }

  loadRows() {
    this.post('/api/relatedentities/rows', this.body(), (response: any) => {
      this.rows = (response && response.Rows) ? response.Rows : [];
      this.columns = (response && response.Columns) ? response.Columns : [];
      this.selectedTargetId = this.rows.length > 0 ? this.rows[0].TargetId : null;
    });
  }

  loadOptions() {
    this.post('/api/relatedentities/options', this.body(), (response: any) => {
      this.options = (response && response.Items) ? response.Items : [];
    });
  }

  /**
   * Combobox. The whole value of the relationship is one call, so the record never passes through
   * a state with no link at all because a delete succeeded and an insert had not run yet.
   */
  onComboboxChange(value: number) {
    if (!this.canEdit || !this.hasParent) {
      return;
    }

    this.post('/api/relatedentities/set', this.body({ TargetId: value == null ? null : Number(value) }),
      () => this.loadRows());
  }

  addRow() {
    if (!this.canEdit || !this.hasParent || this.pendingTargetId == null) {
      return;
    }

    this.post('/api/relatedentities/add', this.body({ TargetId: Number(this.pendingTargetId) }), () => {
      this.pendingTargetId = null;
      this.loadRows();
    });
  }

  removeRow(row: LinkRow) {
    if (!this.canEdit || !this.hasParent) {
      return;
    }

    this.post('/api/relatedentities/remove', this.body({ RowId: row.Id }), () => this.loadRows());
  }

  trackRow(index: number, row: LinkRow) {
    return row.Id;
  }
}
